import { Action, Actions, ofActionSuccessful, State, StateContext, Store } from '@ngxs/store';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { LoadActiveSportsAction } from '../../sports/';
import { SportEventDefinitionDto, SportsService } from '@smatchy/sdk';

import { Injectable } from '@angular/core';
import {
  LoadSportEventDefinitionAction,
  LoadSportEventDefinitionsAction,
  ResetSportEventDefinitionAction,
} from './sport-event-definition.actions';

export interface SportEventDefinitionStateModel {
  /** null until a sport's definition is loaded, which is what the defaults set. */
  sportEventDefinition: SportEventDefinitionDto | null;
  loadingSportSession: boolean;
  sportIdSelected: string;
  allSportsEventDefinition: SportEventDefinitionDto[];
}

const defaults: SportEventDefinitionStateModel = {
  sportEventDefinition: null,
  loadingSportSession: false,
  sportIdSelected: '',
  allSportsEventDefinition: [],
};

@State<SportEventDefinitionStateModel>({
  name: 'sportEventDefinition',
  defaults,
})
@Injectable()
export class SportEventDefinitionState {
  /**
   * Whether the definitions of all the sports are loaded or on their way.
   *
   * A definition is configuration that does not move during a session, yet the
   * list of active sports is reloaded each time the profile is (every visit to
   * Explore among them), and each reload emptied the definitions and asked the
   * server for every sport's again, one /sport-events/:id call per sport in a
   * burst (CAPACITOR-Y, 17 people, growing daily). They are now asked for once,
   * and in a single call; a failure forgets it so the next reload tries again.
   */
  private _allRequested = false;

  constructor(
    private readonly _sportService: SportsService,
    private readonly _actions: Actions,
    private readonly _store: Store,
  ) {
    this._actions.pipe(ofActionSuccessful(LoadActiveSportsAction)).subscribe(() => {
      this._store.dispatch(new LoadSportEventDefinitionsAction());
    });
  }

  @Action(ResetSportEventDefinitionAction)
  resetSportEventDefinitionAction({ patchState }: StateContext<SportEventDefinitionStateModel>) {
    this._allRequested = false;
    patchState({
      allSportsEventDefinition: [],
    });
  }

  @Action(LoadSportEventDefinitionsAction)
  loadSportEventDefinitionsAction({ patchState }: StateContext<SportEventDefinitionStateModel>) {
    if (this._allRequested) {
      return;
    }
    this._allRequested = true;

    return this._sportService.listSportEventDefinitions().subscribe({
      next: (res) => {
        if (!carriesAValue(res)) {
          this._allRequested = false;
          console.error(res.error);
          return;
        }
        patchState({ allSportsEventDefinition: res.value.sportEventDefinitions.filter((d) => !!d) });
      },
      error: (error) => {
        this._allRequested = false;
        console.error(error);
      },
    });
  }

  /**
   * The person picked a sport: its definition becomes the current one. It is
   * read from what the single call above brought back when it is there, and
   * asked for on its own only when it is not (a sport added since, or a first
   * call that failed).
   */
  @Action(LoadSportEventDefinitionAction)
  async loadSportEventDefinitionAction(
    { patchState, getState }: StateContext<SportEventDefinitionStateModel>,
    { sportId }: LoadSportEventDefinitionAction,
  ) {
    const known = getState().allSportsEventDefinition.find((definition) => definition.sportId === sportId);
    if (known) {
      patchState({ sportIdSelected: sportId, loadingSportSession: false, sportEventDefinition: known });
      return;
    }

    patchState({
      loadingSportSession: true,
    });

    return this._sportService
      .getSportEventDefinition({
        sportId,
      })
      .subscribe({
        next: (res) => {
          if (!carriesAValue(res)) {
            patchState({ loadingSportSession: false });
            console.error(res.error);
            return;
          }
          const definition = res.value.sportEventDefinition;
          patchState({
            sportIdSelected: sportId,
            loadingSportSession: false,
            sportEventDefinition: definition,
            allSportsEventDefinition: [
              ...getState().allSportsEventDefinition.filter((d) => d.sportId !== definition.sportId),
              definition,
            ],
          });
        },
        error: (error) => {
          patchState({ loadingSportSession: false });
          console.error(error);
        },
      });
  }
}
